import { useCallback, useState } from "react";
import { buildPlanDrawingData } from "../../components/builder/PlanView/planDrawingData";
import {
	SHARE_SNAPSHOT_OPTIONS,
	type ViewerSnapshotOptions,
} from "../../components/builder/viewerSnapshot";
import {
	type BuildProgress,
	getProjectBuildGuide,
} from "../../domain/build/getProjectBuildGuide";
import type { FinishConfig } from "../../domain/finishes/types";
import type { SiteConditions } from "../../domain/foundation/siteConditions";
import type { WallOpening } from "../../domain/framing/generateFramedWall";
import type { calculateHeaterRequirements } from "../../domain/heater/calculateHeaterRequirements";
import type { IndoorRoomConfig } from "../../domain/indoor/indoorRoom";
import type { InteriorLayout } from "../../domain/interior/types";
import type {
	ClimateProfile,
	LocationMode,
	Market,
} from "../../domain/location/types";
import type { SaunaRotationDeg } from "../../domain/viewer/saunaRotation";
import type {
	BuildInput,
	CalculationResult,
	DeckConfig,
	MaterialViewMode,
	RoofConfig,
	TwoRoomConfig,
	VisibilityFlags,
} from "../../engines/types";
import { useRenderPhoto } from "../../hooks/useRenderPhoto";
import { trackConversionEvent } from "../../lib/conversionTelemetry";
import { buildShareUrl, type ShareableConfig } from "../../lib/shareConfig";
import { useBuilderStore } from "../../store/useBuilderStore";
import { buildShareableConfig } from "./draftConfig";

import type { SaunaType } from "../../types/design";

/**
 * Everything the share dialog needs that only the action layer can produce.
 * Non-null exactly while the dialog is open, so the lazy modal is mounted
 * conditionally rather than rendered with `open={false}` (which would defeat
 * the code split — see
 * docs/notes/2026-07-12-builder-bundle-splits-require-complete-import-boundaries.md).
 */
export interface ShareDialogState {
	url: string;
	/** PNG data URL from the live viewer canvas, or null when unavailable. */
	previewImage: string | null;
	/**
	 * Everything the dialog renders as a "here's my design" summary is frozen
	 * here at open time, alongside the url/image above. The dialog stays
	 * mounted while the builder keeps running — an undo, or any other edit,
	 * while it's open must not update the name/size/price/heat-up shown next
	 * to a URL and preview that still describe the PRE-undo design.
	 */
	projectName: string;
	dimensions: { width: number; length: number };
	estimatedTotal: number;
	heatUpLabel: string | null;
}

interface BuilderActionsOptions {
	type: SaunaType;
	projectName: string;
	market: Market;

	locationMode: LocationMode;
	climateProfile: ClimateProfile;
	postalCode: string;
	dimensions: { width: number; length: number };
	partitionSplit: number;
	twoRoomConfig?: Partial<TwoRoomConfig>;
	rotationDeg: SaunaRotationDeg;
	/** The wizard's "2/4/6 personer" size-tier choice, if any still applies. */
	wizardCapacityHint: number | null;
	visibility: VisibilityFlags;
	openings: WallOpening[];
	deckConfig: DeckConfig | null;
	roofConfig: RoofConfig;
	thermalInputs: {
		glassAreaSqm: number;
		stoneAreaSqm: number;
		isOutdoorLogCabin: boolean;
		heaterType: ShareableConfig["heaterType"];
		heaterModelId?: string | null;
		includesShower: boolean;
		saunaLighting?: ShareableConfig["saunaLighting"];
	};
	buildProgress: BuildProgress;
	siteConditions: SiteConditions;
	/** `null` when the user has authored no interior; the engine generates one. */
	interiorLayout: InteriorLayout | null;
	finishes: FinishConfig;
	/** Chosen wall height (issue 155); `null` = the default. */
	wallHeightM?: number | null;
	/** Inomhus only (issues 174/175); `null` = free-standing. */
	indoorRoom?: IndoorRoomConfig | null;
	/** Material cost estimate in SEK — frozen into the share dialog at open time. */
	estimatedTotal: number;
	/** Preformatted heat-up label from the running-cost summary, or null. */
	heatUpLabel: string | null;
	setVisibility: (visibility: VisibilityFlags) => void;
	setOpenings: (
		openings: WallOpening[] | ((previous: WallOpening[]) => WallOpening[]),
	) => void;
	engineLabel: string;
	calcData: CalculationResult;
	buildInput: BuildInput;
	heater: ReturnType<typeof calculateHeaterRequirements>;
}

export function useBuilderActions({
	type,
	projectName,
	market,

	dimensions,
	twoRoomConfig,
	rotationDeg,
	visibility,
	openings,
	roofConfig,
	thermalInputs,
	buildProgress,
	siteConditions,
	estimatedTotal,
	heatUpLabel,
	setVisibility,
	setOpenings,
	engineLabel,
	calcData,
	buildInput,
	heater,
}: BuilderActionsOptions) {
	const [materialViewMode, setMaterialViewMode] =
		useState<MaterialViewMode>("phase");
	const [summaryOpen, setSummaryOpen] = useState(false);
	const [shareDialog, setShareDialog] = useState<ShareDialogState | null>(null);
	const [snapshotGetter, setSnapshotGetter] = useState<
		((options?: ViewerSnapshotOptions) => string | null) | null
	>(null);
	const [isDraggingWindow, setIsDraggingWindow] = useState<{
		widthMm: number;
		heightMm: number;
	} | null>(null);
	const {
		renderLoading,
		renderResult,
		renderError,
		renderPhoto,
		closeRenderResult,
	} = useRenderPhoto();

	const handleSnapshotReady = useCallback(
		(nextSnapshotGetter: () => string | null) => {
			setSnapshotGetter(() => nextSnapshotGetter);
		},
		[],
	);

	const handleVisibilityToggle = useCallback(
		(key: keyof VisibilityFlags) => {
			setVisibility({ ...visibility, [key]: !visibility[key] });
		},
		[setVisibility, visibility],
	);

	const handleExportPdf = useCallback(async () => {
		const [{ exportPDF }, { createBuildStepSnapshotProvider }] =
			await Promise.all([
				import("../../lib/exportPDF"),
				import("../../lib/buildStepSnapshots"),
			]);
		const snapshots = createBuildStepSnapshotProvider(buildInput, type);
		try {
			await exportPDF({
				projectName,
				engineLabel,
				market,

				width: dimensions.width,
				length: dimensions.length,
				...(type === "tvarum" ? twoRoomConfig : {}),
				roofConfig,
				materials: calcData.materials,
				cuts: calcData.cuts,
				renderImageDataUrl: snapshotGetter?.() ?? undefined,
				openings,
				heater,
				siteConditions,
				rotationDeg,
				materialsByPhase: calcData.materialsByPhase,
				productRecommendations: calcData.productRecommendations,
				chimney: calcData.chimney,
				timeline: calcData.timeline,
				buildGuide: getProjectBuildGuide({
					data: calcData,
					width: dimensions.width,
					length: dimensions.length,
					openings,
					thermalInputs,
					market,
					engineType: type,
				}),
				buildProgress,
				saunaType: type,
				buildStepSnapshotProvider: snapshots.provider,
				materialViewMode,
				pricingSummary: calcData.pricingSummary,
				planDrawing: buildPlanDrawingData(buildInput),
			});
			trackConversionEvent("funnel_pdf_exported", {
				market,
				sauna_type: type,
			});
		} finally {
			snapshots.dispose();
		}
	}, [
		buildInput,
		calcData,
		dimensions.length,
		dimensions.width,
		engineLabel,
		heater,
		market,
		materialViewMode,
		openings,
		projectName,
		roofConfig,
		// Must be listed even though the PDF's other inputs rarely move with it:
		// on a FLAT site a rotation leaves `buildInput` (and every other dep)
		// referentially untouched, so without this the callback is never rebuilt
		// and the export draws the orientation the page was opened with.
		rotationDeg,
		siteConditions,
		snapshotGetter,
		buildProgress,
		thermalInputs,
		twoRoomConfig,
		type,
	]);

	const handleRenderPhoto = useCallback(async () => {
		await renderPhoto({
			width: dimensions.width,
			length: dimensions.length,
			saunaType: type,
		});
	}, [dimensions.length, dimensions.width, renderPhoto, type]);

	const handleShare = useCallback(() => {
		// Same snapshot as the device-local draft: one builder, so a field added
		// to the design travels with both.
		const shareConfig: ShareableConfig = buildShareableConfig(
			useBuilderStore.getState(),
			type,
		);

		const url = buildShareUrl(shareConfig);

		// Reuse the PDF hero-image path: an explicit render of the LIVE canvas
		// followed by toDataURL, with the analysis colouring neutralised. The
		// getter is absent before the renderer boots and returns null once the
		// context is gone, so a missing image is a normal state here, not an
		// error — the dialog simply opens without one.
		let previewImage: string | null = null;
		try {
			previewImage = snapshotGetter?.(SHARE_SNAPSHOT_OPTIONS) ?? null;
		} catch {
			previewImage = null;
		}

		setShareDialog({
			url,
			previewImage,
			projectName,
			dimensions,
			estimatedTotal,
			heatUpLabel,
		});
	}, [
		dimensions,
		estimatedTotal,
		heatUpLabel,
		projectName,
		snapshotGetter,
		type,
	]);

	const handleCloseShareDialog = useCallback(() => {
		setShareDialog(null);
	}, []);

	const handleStartWindowDrag = useCallback(
		(widthMm: number, heightMm: number) => {
			setIsDraggingWindow({ widthMm, heightMm });
		},
		[],
	);

	const handleWindowPlaced = useCallback(
		(opening: WallOpening) => {
			setOpenings((previous) => [...previous, opening]);
			setIsDraggingWindow(null);
		},
		[setOpenings],
	);

	const handleDragCancelled = useCallback(() => {
		setIsDraggingWindow(null);
	}, []);

	return {
		materialViewMode,
		setMaterialViewMode,
		summaryOpen,
		setSummaryOpen,
		shareDialog,
		isDraggingWindow,
		renderLoading,
		renderResult,
		renderError,
		closeRenderResult,
		handleSnapshotReady,
		handleVisibilityToggle,
		handleExportPdf,
		handleRenderPhoto,
		handleShare,
		handleCloseShareDialog,
		handleStartWindowDrag,
		handleWindowPlaced,
		handleDragCancelled,
	};
}
